<template>
  <div class="b2b-chat">
    <button v-if="!open" class="btn secondary-color chat-launch" @click="open = true"
      aria-label="Abrir GI.IA, assistente do B2B">
      <span class="chat-avatar chat-avatar-small" aria-hidden="true">
        <img v-if="showAvatarGi" :src="avatarGi" alt="" @error="showAvatarGi = false">
        <i v-else class="material-icons">assistant</i>
      </span>
      <span>GI.IA · Assistente B2B</span>
    </button>
    <section v-else class="chat-panel" role="dialog" aria-label="GI.IA, assistente do B2B" @keydown.esc="open = false">
      <div class="chat-heading secondary-color white-text">
        <div class="chat-identity">
          <span class="chat-avatar" aria-hidden="true">
            <img v-if="showAvatarGi" :src="avatarGi" alt="" @error="showAvatarGi = false">
            <i v-else class="material-icons">assistant</i>
          </span>
          <div class="chat-title"><strong>GI.IA</strong><span>Assistente B2B</span></div>
        </div>
        <button class="chat-close" @click="open = false" aria-label="Fechar assistente">×</button>
      </div>
      <div class="chat-tabs">
        <button type="button" :aria-pressed="!quotesOpen" @click="quotesOpen = false">Conversar</button>
        <button type="button" :aria-pressed="quotesOpen" @click="quotesOpen = true">Fazer orçamento</button>
      </div>
      <div class="chat-content" ref="content" role="log" aria-live="polite" aria-relevant="additions">
        <chat-quote-panel v-if="quotesOpen" />
        <div v-show="!quotesOpen">
        <p class="chat-hint">Consulte peças, notas e pedidos disponíveis para seu acesso.
          As respostas usam IA; confira as informações nas telas do B2B.</p>
        <p class="chat-hint" v-if="cliente">Cliente selecionado: {{ cliente.clieNome }}</p>
        <div v-if="messages.length === 0" class="chat-suggestions">
          <button v-for="suggestion in suggestions" :key="suggestion" @click="send(suggestion)" :disabled="busy">{{ suggestion }}</button>
        </div>
        <div v-for="(message, index) in messages" :key="index" class="chat-message" :class="message.role">
          <strong>{{ message.role === 'user' ? 'Você' : 'Assistente' }}</strong>
          <p>{{ message.text }}</p>
          <article v-for="(report, reportIndex) in message.reports" :key="'report-' + reportIndex" class="chat-report">
            <strong>{{ report.titulo }}</strong>
            <p>{{ report.escopo }}</p>
            <p>{{ report.inicio }} a {{ report.fim }} · {{ report.criterio }}</p>
            <p>Total dos itens: {{ productPrice(report.totalValor) }}</p>
            <p>{{ report.totalPedidos }} pedidos · {{ report.totalClientes }} clientes com compras</p>
            <ol v-if="report.linhas && report.linhas.length" class="chat-ranking">
              <li v-for="row in report.linhas" :key="row.posicao">
                <strong>{{ row.descricao }}</strong><span v-if="row.codigo"> · {{ row.codigo }}</span>
                <div>{{ productPrice(row.valor) }} · Qtd.: {{ row.quantidade }} · {{ row.pedidos }} pedidos</div>
              </li>
            </ol>
            <p v-else-if="report.parametros.dimensao !== 'resumo'">Nenhuma compra encontrada neste período.</p>
            <p class="chat-report-note">{{ report.base }}</p>
            <p class="chat-report-note">{{ report.carteira ? 'Carteira: uso interno. Para enviar a um cliente, selecione-o no B2B e peça o relatório desse cliente.' : 'Relatório restrito ao cliente selecionado.' }}</p>
            <p class="chat-report-note">Top 10; totais do período completo. O download recalcula os dados com seu acesso atual.</p>
            <div class="chat-document-actions">
              <button type="button" :disabled="!!downloading" @click="downloadReport(report, 'pdf')">Baixar PDF</button>
              <button type="button" :disabled="!!downloading" @click="downloadReport(report, 'pptx')">Baixar PowerPoint</button>
            </div>
          </article>
          <article v-for="product in message.products" :key="'product-' + product.id" class="chat-product">
            <div class="chat-product-top">
              <img v-if="!product.photoFailed" :src="productPhoto(product)" :alt="product.descricao || 'Foto do produto'"
                loading="lazy" @error="product.photoFailed = true">
              <span v-else class="chat-product-no-photo"><i class="material-icons" aria-hidden="true">image</i>Sem foto</span>
              <div>
                <strong>{{ product.descricao }}</strong>
                <p>Código: {{ product.codigo }}</p>
                <p v-if="product.codigoFabricante">Fabricante: {{ product.codigoFabricante }}</p>
                <p v-if="product.marca">{{ product.marca }}</p>
              </div>
            </div>
            <p class="chat-product-price">{{ productPrice(product.precoTabela) }}<span v-if="product.unidade"> / {{ product.unidade }}</span></p>
            <p class="chat-product-note">Preço de tabela. Confira impostos, frete e condições no pedido.</p>
            <p v-if="product.aplicacaoPendente" class="chat-product-note">Candidato por aplicação: confirme ano, motor, versão e posição.</p>
            <button type="button" class="chat-product-open" @click="openProduct(product)">Abrir produto no B2B</button>
          </article>
          <div v-for="document in message.documents" :key="document.id" class="chat-document">
            <strong>NF-e {{ document.numero }} / {{ document.serie }}</strong>
            <p v-if="document.empresa">{{ document.empresa }}</p>
            <p v-if="document.emissao">Emissão: {{ document.emissao }}</p>
            <p v-if="document.cancelada" class="chat-error">Nota cancelada</p>
            <div class="chat-document-actions">
              <button type="button" :disabled="!!downloading" @click="download(document, 'danfe')">Baixar DANFE</button>
              <button type="button" :disabled="!!downloading" @click="download(document, 'xml')">Baixar XML</button>
            </div>
          </div>
        </div>
        <p v-if="busy" role="status">Consultando…</p>
        <p v-if="downloading" role="status">Preparando arquivo…</p>
        </div>
      </div>
      <p v-if="error && !quotesOpen" class="chat-error" role="alert">{{ error }}</p>
      <form v-show="!quotesOpen" class="chat-form" @submit.prevent="send(draft)">
        <label for="b2b-chat-question">Sua pergunta</label>
        <textarea id="b2b-chat-question" v-model="draft" maxlength="2000" :disabled="busy" rows="2"
          placeholder="Digite uma pergunta sobre o B2B" @keydown.enter.exact.prevent="send(draft)"></textarea>
        <div class="chat-actions">
          <button type="button" class="chat-clear" @click="clear" :disabled="busy">Limpar</button>
          <button type="submit" class="btn secondary-color" :disabled="busy || !draft.trim()">Enviar</button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import ChatQuotePanel from './ChatQuotePanel.vue';
export default {
  components: { ChatQuotePanel },
  props: { usuario: { type: Object, required: true }, cliente: Object },
  data() {
    return { open: false, quotesOpen: false, draft: '', messages: [], busy: false, error: null, active: true, downloading: null,
      avatarGi: 'webapi/resource/gi-avatar.png', showAvatarGi: true };
  },
  computed: {
    suggestions() {
      const suggestions = ['Qual o status do meu último pedido?', 'Qual o preço e a disponibilidade de um produto?',
        'Preciso da segunda via da DANFE e do XML da minha última nota fiscal.',
        'Qual a pastilha de freio para meu Onix ano 2020?', 'Quais marcas eu mais comprei nos últimos 90 dias?'];
      if (['ROOT', 'ADMIN', 'REPRESENTANTE', 'PREPOSTO_REPRESE', 'VENDEDOR_INTERNO', 'VENDEDOR_EXTERNO'].includes(this.usuario.usuaRole)) {
        suggestions.push('Quanto vendemos neste mês?', 'Quais pedidos estão pendentes?', 'Quais os top clientes da minha carteira neste mês?',
          'Quais os produtos mais comprados da minha carteira nos últimos 90 dias?');
      }
      return suggestions;
    }
  },
  beforeDestroy() { this.active = false; },
  methods: {
    downloadReport(report, format) {
      if (this.downloading || !report.parametros || !['pdf', 'pptx'].includes(format)) return;
      this.downloading = 'report:' + format;
      this.error = null;
      this.$http.post('chat/relatorios/' + format, report.parametros, { responseType: 'blob', timeout: 120000 }).then(response => {
        if (!this.active) return;
        const type = (response.headers.get('Content-Type') || '').toLowerCase();
        const expected = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
        if (!type.startsWith(expected)) throw new Error('Invalid report response');
        const url = URL.createObjectURL(response.body);
        const link = window.document.createElement('a');
        link.href = url; link.download = 'relatorio_compras.' + format;
        window.document.body.appendChild(link); link.click(); window.document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }).catch(response => {
        if (!this.active) return;
        this.error = [401, 403, 409].includes(response.status)
          ? 'Confira sua sessão e o cliente selecionado e faça a consulta novamente.'
          : 'Não foi possível gerar o relatório. Tente novamente em instantes.';
      }).then(() => { if (this.active) this.downloading = null; });
    },
    productPhoto(product) { return 'webapi/item/' + product.id + '/fotoLow'; },
    productPrice(value) {
      if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return 'Preço indisponível';
      return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    openProduct(product) {
      if (!Number.isSafeInteger(product.id) || product.id <= 0) return;
      this.$router.push({ path: '/unit050', query: { chatItemId: String(product.id) } });
      this.open = false;
    },
    clear() { this.messages = []; this.error = null; this.draft = ''; },
    download(document, format) {
      if (this.downloading || !Number.isSafeInteger(document.id) || document.id <= 0 || !['danfe', 'xml'].includes(format)) return;
      this.downloading = document.id + ':' + format;
      this.error = null;
      // Authenticated request to a fixed same-origin endpoint. No model-provided URL or browser token in links.
      this.$http.get('chat/notas/' + document.id + '/' + format, { responseType: 'blob', timeout: 120000 })
        .then(response => {
          if (!this.active) return;
          const type = response.headers.get('Content-Type') || '';
          if (!type.toLowerCase().startsWith(format === 'xml' ? 'application/xml' : 'application/pdf'))
            throw new Error('Invalid document response');
          const url = URL.createObjectURL(response.body);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = 'nfe_' + document.id + (format === 'xml' ? '.xml' : '.pdf');
          window.document.body.appendChild(link);
          link.click();
          window.document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }).catch(response => {
          if (!this.active) return;
          this.error = response.status === 404 || response.status === 401 || response.status === 403
            ? 'Documento indisponível para seu acesso. Confira a sessão e o cliente selecionado.'
            : 'Não foi possível baixar o documento fiscal. Tente novamente ou consulte a tela de pedidos.';
        }).then(() => { if (this.active) this.downloading = null; });
    },
    scroll() { this.$nextTick(() => { if (this.$refs.content) this.$refs.content.scrollTop = this.$refs.content.scrollHeight; }); },
    send(text) {
      if (this.busy || !text || !text.trim()) return;
      const question = text.trim();
      if (question.length > 2000) return;
      this.busy = true;
      this.error = null;
      this.draft = '';
      const previousQuestions = this.messages.filter(message => message.role === 'user').slice(-4).map(message => message.text);
      // Only previous questions provide context; prior answers and identity/scope are never sent from the browser.
      this.messages = this.messages.slice(-18).concat([{ role: 'user', text: question }]);
      this.scroll();
      this.$http.post('chat', { message: question, previousQuestions }, { timeout: 150000 }).then(response => {
        if (!this.active) return;
        this.messages.push({ role: 'assistant', text: response.body.answer,
          reports: (response.body.reports || []).filter(report => report && report.parametros && Array.isArray(report.linhas)).slice(0, 4),
          products: (response.body.products || []).filter(product => Number.isSafeInteger(product.id) && product.id > 0)
            .slice(0, 10).map(product => Object.assign({}, product, { photoFailed: false })),
          documents: (response.body.documents || []).filter(document => Number.isSafeInteger(document.id) && document.id > 0).slice(0, 10) });
      }).catch(response => {
        if (!this.active) return;
        this.draft = question;
        this.error = response.body && response.body.error
          ? response.body.error : 'Não foi possível consultar o assistente. Verifique sua sessão e tente novamente.';
      }).then(() => { if (this.active) { this.busy = false; this.scroll(); } });
    }
  }
};
</script>

<style scoped>
.b2b-chat { position: fixed; right: 18px; bottom: 18px; z-index: 990; color: #263238; font-size: 14px; }
.chat-launch { border-radius: 24px; display: flex; align-items: center; gap: 8px; height: 48px; padding: 0 16px 0 8px; }
.chat-identity { display: flex; align-items: center; gap: 10px; }
.chat-avatar { width: 44px; height: 44px; flex-shrink: 0; border-radius: 50%; overflow: hidden; background: #fff; color: #167c47; display: flex; align-items: center; justify-content: center; }
.chat-avatar img { display: block; width: 100%; height: 100%; object-fit: contain; object-position: center bottom; }
.chat-avatar .material-icons { float: none; width: auto; height: auto; line-height: 1; font-size: 26px; }
.chat-avatar-small { width: 32px; height: 32px; }
.chat-avatar-small .material-icons { font-size: 22px; }
.chat-title { display: flex; flex-direction: column; line-height: 1.3; }
.chat-title strong { font-size: 17px; }
.chat-title span { font-size: 12px; opacity: .9; }
.chat-panel { width: 380px; max-width: calc(100vw - 24px); height: 590px; max-height: calc(100vh - 36px); background: white; border-radius: 12px; box-shadow: 0 4px 24px #0004; display: flex; flex-direction: column; overflow: hidden; }
.chat-heading { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; }
.chat-tabs { display: flex; border-bottom: 1px solid #cfd8dc; }
.chat-tabs button { flex: 1; border: 0; background: #f5f7f8; padding: 10px; cursor: pointer; color: #37474f; }
.chat-tabs button[aria-pressed="true"] { background: #e3f2fd; border-bottom: 2px solid #1565c0; }
.chat-close { background: transparent; color: inherit; border: 0; cursor: pointer; font-size: 26px; }
.chat-content { flex: 1; overflow-y: auto; padding: 12px; min-height: 0; }
.chat-hint { font-size: 12px; color: #546e7a; margin: 0 0 12px; }
.chat-suggestions { display: flex; flex-direction: column; gap: 8px; }
.chat-suggestions button { text-align: left; background: #f5f7f8; border: 1px solid #cfd8dc; border-radius: 8px; padding: 10px; cursor: pointer; }
.chat-message { background: #f1f4f6; border-radius: 8px; padding: 10px; margin-bottom: 10px; overflow-wrap: break-word; }
.chat-message.user { background: #e3f2fd; margin-left: 20px; }
.chat-message p { white-space: pre-wrap; margin: 4px 0 0; }
.chat-document { background: white; border: 1px solid #cfd8dc; padding: 10px; margin-top: 8px; border-radius: 6px; }
.chat-report { background: white; border: 1px solid #9cb9a8; padding: 10px; margin-top: 8px; border-radius: 6px; }
.chat-report .chat-report-note { font-size: 11px; color: #546e7a; margin-top: 8px; }
.chat-ranking { list-style: decimal; padding-left: 22px; margin: 12px 0; }
.chat-ranking li { margin: 8px 0; }
.chat-product { background: #fff; border: 1px solid #cfd8dc; border-radius: 8px; padding: 12px; margin-top: 10px; }
.chat-product-top { display: flex; align-items: flex-start; gap: 10px; }
.chat-product-top > div { min-width: 0; }
.chat-product-top > img, .chat-product-no-photo { width: 72px; height: 72px; flex-shrink: 0; object-fit: contain; background: #f5f7f8; border-radius: 6px; }
.chat-product-no-photo { display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 11px; color: #607d8b; }
.chat-product .chat-product-price { margin-top: 10px; font-weight: 700; font-size: 17px; }
.chat-product-price span { font-size: 12px; font-weight: 400; }
.chat-product .chat-product-note { font-size: 11px; color: #546e7a; margin-top: 6px; }
.chat-product-open { width: 100%; margin-top: 10px; padding: 9px; background: #eef5f1; color: #1b5e39; border: 1px solid #9cb9a8; border-radius: 5px; cursor: pointer; }
.chat-document-actions { display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
.chat-document-actions button { border: 1px solid #90a4ae; border-radius: 4px; background: #f5f7f8; padding: 8px; cursor: pointer; }
.chat-error { padding: 8px 12px; margin: 0; color: #b71c1c; }
.chat-form { padding: 12px; border-top: 1px solid #eceff1; }
.chat-form label { color: #455a64; }
.chat-form textarea { resize: none; height: 64px; border: 1px solid #90a4ae; border-radius: 4px; padding: 8px; box-sizing: border-box; }
.chat-actions { display: flex; justify-content: space-between; margin-top: 8px; }
.chat-clear { border: 0; background: transparent; cursor: pointer; color: #455a64; }
button:focus-visible, textarea:focus-visible { outline: 2px solid #1565c0; outline-offset: 2px; }
@media (max-width: 600px) { .b2b-chat { right: 12px; bottom: 12px; } }
</style>



// WEBPACK FOOTER //
// B2bChat.vue?39521708